import { AccentVariantEnum } from '@delmonicos/shared-design-system-react/src/components/accent-icon/accent-icon';
import { Card } from '@delmonicos/shared-design-system-react/src/components/card/card';
import { CopyToClipboard } from '@delmonicos/shared-design-system-react/src/components/copy-to-clipboard/copy-to-clipboard';
import { Divider } from '@delmonicos/shared-design-system-react/src/components/divider/divider';
import {
  Icon,
  IconColor,
  IconSize,
} from '@delmonicos/shared-design-system-react/src/components/icon/icon';
import {
  GapEnum,
  Stack,
} from '@delmonicos/shared-design-system-react/src/components/stack/stack';
import { MiniTitle } from '@delmonicos/shared-design-system-react/src/components/typography/mini-title/mini-title';
import {
  Text,
  TextColor,
  TextFontSize,
} from '@delmonicos/shared-design-system-react/src/components/typography/text/text';
import { AccentTitle } from '@delmonicos/shared-design-system-react/src/components/typography/title/accent-title/accent-title';
import { HeadingEnum } from '@delmonicos/shared-design-system-react/src/models/enums/heading.enum';
import { formatDate } from '@delmonicos/shared-sdk/libs/cpms/src/clients/utils/formatters/format-date';
import { useTranslation } from 'react-i18next';

import i18n from '@app/core/locale/i18n';
import { useEmspBoLocationContext } from '@app/feature/emsp-bo-location/emsp-bo-location.context';

const InfoRow = ({
  icon,
  label,
  value,
}: {
  icon: string;
  label: string;
  value: React.ReactNode;
}): React.JSX.Element => (
  <Stack alignItems="center" justifyContent="space-between">
    <Stack alignItems="center" gap={GapEnum.XS} flex={1}>
      <Icon color={IconColor.Secondary} iconSize={IconSize.Small} name={icon} />
      <MiniTitle color={TextColor.Secondary}>{label}</MiniTitle>
    </Stack>
    <Stack flex={1}>
      <Text fontSize={TextFontSize.Large}>{value ?? '-'}</Text>
    </Stack>
  </Stack>
);

export const LocationInfoCardComponent = (): React.JSX.Element => {
  const { t } = useTranslation();
  const location = useEmspBoLocationContext();

  return (
    <Card flex={1}>
      <Stack direction="column" gap={GapEnum.L}>
        <AccentTitle
          icon="info-circle"
          text={t('$.emsp-bo-location-details.info-card.title')}
          as={HeadingEnum.H2}
          variant={AccentVariantEnum.Primary}
        />
        <InfoRow
          icon="location"
          label={t('delmonicos-shared-sdk.outputs-ocpi-cpo-api-location.name')}
          value={location.name}
        />
        <Divider />
        <InfoRow
          icon="map-01"
          label={t(
            'delmonicos-shared-sdk.outputs-ocpi-cpo-api-location.address',
          )}
          value={location.address}
        />
        <InfoRow
          icon="building-02"
          label={t('delmonicos-shared-sdk.outputs-ocpi-cpo-api-location.city')}
          value={location.city}
        />
        <InfoRow
          icon="mail-01"
          label={t(
            'delmonicos-shared-sdk.outputs-ocpi-cpo-api-location.postal-code',
          )}
          value={location.postal_code}
        />
        <InfoRow
          icon="globe-02"
          label={t(
            'delmonicos-shared-sdk.outputs-ocpi-cpo-api-location.country',
          )}
          value={location.country}
        />
        <Divider />
        <Stack alignItems="center" justifyContent="space-between">
          <Stack alignItems="center" gap={GapEnum.XS} flex={1}>
            <Icon
              color={IconColor.Secondary}
              iconSize={IconSize.Small}
              name="map-01"
            />
            <MiniTitle color={TextColor.Secondary}>
              {t('$.emsp-bo-location-details.info-card.coordinates')}
            </MiniTitle>
          </Stack>
          <Stack flex={1}>
            <CopyToClipboard
              textToCopy={`${location.coordinates.latitude}, ${location.coordinates.longitude}`}>
              <Text fontSize={TextFontSize.Large}>
                {location.coordinates.latitude},{' '}
                {location.coordinates.longitude}
              </Text>
            </CopyToClipboard>
          </Stack>
        </Stack>
        {location.time_zone && (
          <InfoRow
            icon="clock"
            label={t('$.emsp-bo-location-details.info-card.time-zone')}
            value={location.time_zone}
          />
        )}
        <Divider />
        <InfoRow
          icon="calendar-check-02"
          label={t(
            'delmonicos-shared-sdk.outputs-ocpi-cpo-api-location.last-updated',
          )}
          value={formatDate(location.last_updated, i18n)}
        />
      </Stack>
    </Card>
  );
};
